import React from "react";
import { cn } from "@/lib/utils";
import { pick } from "@/lib/languages";
import { useSettings } from "@/context/SettingsContext";
import { dsL } from "@/i18n/ds";
import { RESULT_ASIDE, RESULT_BODY, RESULT_FRAME, RESULT_MEDIA } from "./resultCardLayout";
import "./ds.css";

/**
 * Loading in the page's own layout: pale shapes where the content will be,
 * under the search summary, instead of a full-screen loader reciting steps.
 * Shapes are hidden from assistive technology; the SkeletonGroup around
 * them says "Chargement…" once (role=status). The shimmer is off under
 * prefers-reduced-motion (ds.css).
 *
 *   <SkeletonGroup>{[0, 1, 2].map((i) => <SkeletonResultRow key={i} />)}</SkeletonGroup>
 */
export function Skeleton({ className, style }) {
  return <span aria-hidden="true" className={cn("tk-ds-shimmer block rounded-sm", className)} style={style} />;
}

/** One line of text. `size`: md (a 14 px line) or lg (a title). `width`: any CSS length. */
export function SkeletonLine({ width = "100%", size = "md", className }) {
  return <Skeleton className={cn(size === "lg" ? "h-4" : "h-3", className)} style={{ width }} />;
}

/** A photo, a map or a panel: give it its size or aspect ratio. */
export function SkeletonBlock({ className }) {
  return <Skeleton className={cn("rounded-lg", className)} />;
}

/** A grid card: photo on top, three lines below. */
export function SkeletonCard({ className }) {
  return (
    <div aria-hidden="true" className={cn("flex flex-col gap-3 rounded-lg border border-touki bg-white p-3", className)}>
      <Skeleton className="aspect-[4/3] w-full" />
      <SkeletonLine width="62%" size="lg" />
      <SkeletonLine width="44%" />
      <SkeletonLine width="30%" />
    </div>
  );
}

/** The placeholder of one ResultCard, in its exact frame. `media={false}` for a flight. */
export function SkeletonResultRow({ media = true, className }) {
  return (
    <div aria-hidden="true" data-testid="skeleton-result-row" className={cn(RESULT_FRAME, className)}>
      {media ? <Skeleton className={RESULT_MEDIA} /> : null}
      <div className={cn(RESULT_BODY, "gap-2.5 pt-1")}>
        <SkeletonLine width="52%" size="lg" />
        <SkeletonLine width="34%" />
        <SkeletonLine width="42%" />
      </div>
      <div className={RESULT_ASIDE}>
        <div className="flex flex-col gap-2 sm:items-end">
          <SkeletonLine width="5.5rem" size="lg" />
          <SkeletonLine width="8rem" />
        </div>
        <Skeleton className="h-10 w-28 rounded-lg" />
      </div>
    </div>
  );
}

/** The region that says, once, that this part of the page is loading. */
export function SkeletonGroup({ label, children, className, "data-testid": testId = "skeleton-group" }) {
  const { lang } = useSettings() || {};
  return (
    <div role="status" data-testid={testId} className={cn("flex flex-col gap-3", className)}>
      <span className="sr-only">{label ?? pick(dsL, lang).loading}</span>
      {children}
    </div>
  );
}

export default Skeleton;
